import React from 'react';
import type { Video } from '../services/videoService';

interface VideoCardProps {
  video: Video;
  onSelect?: (video: Video) => void;
  onDelete?: (video: Video) => void;
  selected?: boolean;
}

const VideoCard: React.FC<VideoCardProps> = ({ video, onSelect, onDelete, selected }) => {
  // Format duration from seconds to HH:MM:SS
  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '--:--';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Format file size
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '--';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  // Get thumbnail URL (prefer preview, fallback to full, then placeholder)
  const getThumbnailUrl = (): string => {
    if (video.thumbnail_preview_url) return video.thumbnail_preview_url;
    if (video.thumbnail_full_url) return video.thumbnail_full_url;
    // Placeholder image
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="180" viewBox="0 0 320 180"%3E%3Crect width="320" height="180" fill="%23e5e7eb"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="system-ui" font-size="16" fill="%239ca3af"%3ENo Thumbnail%3C/text%3E%3C/svg%3E';
  };

  const statusColors = {
    ready: 'bg-green-100 text-green-800',
    processing: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
  };

  const sourceTypeIcons: Record<string, React.ReactNode> = {
    upload: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
    ),
    remote: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>
    ),
    onestudio: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm4 0h-2v-6h2v6zm0-8H9V7h6v2z" />
      </svg>
    ),
    ottstudio: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zM9 8h2v8H9zm4 0h2v8h-2z" />
      </svg>
    ),
  };

  // Source badge colors and labels for thumbnail overlay
  const sourceTypeBadge: Record<string, { color: string; label: string } | undefined> = {
    onestudio: { color: 'bg-blue-500 text-white', label: '1Studio' },
    ottstudio: { color: 'bg-purple-500 text-white', label: 'OTT' },
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-md overflow-hidden transition-all duration-200 hover:shadow-lg ${
        selected ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      {/* Thumbnail */}
      <div
        className="relative aspect-video bg-gray-200 cursor-pointer group"
        onClick={() => onSelect?.(video)}
      >
        <img
          src={getThumbnailUrl()}
          alt={video.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {/* Duration badge */}
        {video.duration && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
            {formatDuration(video.duration)}
          </div>
        )}

        {/* Play button overlay on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
          <svg
            className="w-16 h-16 text-white opacity-0 group-hover:opacity-90 transition-opacity duration-200"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>

        {/* Status badge + Source badge */}
        <div className="absolute top-2 left-2 flex gap-1">
          <div className={`px-2 py-1 rounded text-xs font-medium ${statusColors[video.status]}`}>
            {video.status}
          </div>
          {sourceTypeBadge[video.source_type] && (
            <div className={`px-2 py-1 rounded text-xs font-medium ${sourceTypeBadge[video.source_type]!.color}`}>
              {sourceTypeBadge[video.source_type]!.label}
            </div>
          )}
        </div>
      </div>

      {/* Card content */}
      <div className="p-4">
        {/* Title */}
        <h3
          className="font-semibold text-gray-900 mb-2 line-clamp-2 cursor-pointer hover:text-blue-600"
          onClick={() => onSelect?.(video)}
          title={video.name}
        >
          {video.name}
        </h3>

        {/* Description */}
        {video.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2" title={video.description}>
            {video.description}
          </p>
        )}

        {/* Metadata */}
        <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-3">
          {video.resolution_width && video.resolution_height && (
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {video.resolution_width}x{video.resolution_height}
            </span>
          )}
          {video.file_size && (
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {formatFileSize(video.file_size)}
            </span>
          )}
          {video.format && (
            <span className="uppercase">{video.format}</span>
          )}
        </div>

        {/* Tags */}
        {video.tags && video.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {video.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
              >
                {tag}
              </span>
            ))}
            {video.tags.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                +{video.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          {/* Source type */}
          <div className="flex items-center gap-1 text-xs text-gray-500">
            {sourceTypeIcons[video.source_type]}
            <span className="capitalize">
              {video.source_type === 'onestudio' ? '1Studio' : 
               video.source_type === 'ottstudio' ? 'OTT-Studio' : 
               video.source_type}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect?.(video);
              }}
              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
              title="View details"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Delete "${video.name}"?`)) {
                    onDelete(video);
                  }
                }}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete video"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoCard;
